<script lang="ts" setup>
import { taskApi, type TaskAppRecordItem } from '@/api/task'

interface PartnerItem {
  id: string
  title: string
  icon: string
}

const staticPartners: PartnerItem[] = [
  { id: 'static-mobile-1', title: '中国移动', icon: useOssImage('/img/partner/zgyd.png') },
  { id: 'static-telecom-1', title: '中国电信', icon: useOssImage('/img/partner/zgdx.png') },
  { id: 'static-walmart', title: '沃尔玛', icon: useOssImage('/img/partner/wem.png') },
  { id: 'static-suning', title: '苏宁易购', icon: useOssImage('/img/partner/snyg.png') },
  { id: 'static-yonghui', title: '永辉超市', icon: useOssImage('/img/partner/yhcs.png') },
  { id: 'static-mobile-2', title: '中国移动', icon: useOssImage('/img/partner/zgyd.png') },
  { id: 'static-unicom', title: '中国联通', icon: useOssImage('/img/partner/zglt.png') },
  { id: 'static-tmall', title: '天猫', icon: useOssImage('/img/partner/tm.png') },
  { id: 'static-hema', title: '盒马', icon: useOssImage('/img/partner/hm.png') },
  { id: 'static-jd', title: '京东', icon: useOssImage('/img/partner/jd.jpg') },
  { id: 'static-meituan', title: '美团', icon: useOssImage('/img/partner/mt.png') },
  { id: 'static-telecom-2', title: '中国电信', icon: useOssImage('/img/partner/zgdx.png') },
]

// 第一屏始终保留原型中的静态合作伙伴，后面三屏再填充接口数据。
const partnerPages = ref<PartnerItem[][]>([staticPartners, [], [], []])
const token = useCookie<string | null>('auth_token')

function pickText(item: TaskAppRecordItem, keys: string[]) {
  for (const key of keys) {
    const value = item[key]
    if (value != null && String(value).trim()) return String(value).trim()
  }
  return ''
}

function normalizePartner(item: TaskAppRecordItem, pageNum: number, index: number): PartnerItem {
  return {
    id: pickText(item, ['taskAppRecordId', 'appRecordId', 'id']) || pageNum + '-' + index,
    title: pickText(item, ['appName', 'taskAppName', 'name', 'title']),
    icon: pickText(item, ['appIcon', 'taskAppIcon', 'icon', 'appLogo', 'logo', 'image']),
  }
}

async function fetchPartnerPages() {
  // 合作伙伴接口需要登录，未登录时只展示第一屏静态数据，避免 401 被全局拦截器跳转到登录页。
  if (!token.value) return

  const dynamicPages = await Promise.all(
    [1, 2, 3].map(async pageNum => {
      try {
        const response = await taskApi.getTaskAppRecordPage({ pageNum, pageSize: 12 })
        return (response.rows || [])
          .slice(0, 12)
          .map((item, index) => normalizePartner(item, pageNum, index))
          .filter(item => item.title || item.icon)
      } catch (error) {
        console.error('获取合作伙伴列表失败', { pageNum, error })
        return []
      }
    })
  )
  partnerPages.value = [staticPartners, ...dynamicPages]
}

onMounted(fetchPartnerPages)
</script>
<template>
  <div class="partners-panel">
    <el-carousel
      indicator-position="outside"
      arrow="never"
      height="184px"
      class="partners-carousel"
    >
      <el-carousel-item
        v-for="(partners, pageIndex) in partnerPages"
        :key="pageIndex"
      >
        <div class="partners-grid">
          <div class="partner-item" v-for="item in partners" :key="item.id">
            <div class="partner-icon">
              <img v-if="item.icon" :src="item.icon" :alt="item.title" />
            </div>
            <span class="partner-name" :title="item.title">{{ item.title }}</span>
          </div>
        </div>
      </el-carousel-item>
    </el-carousel>
  </div>
</template>
<style lang="scss" scoped>
.partners-panel {
  width: 100%;
  min-height: 220px;
}

.partners-carousel {
  :deep(.el-carousel__indicators--outside) {
    margin-top: 8px;
  }

  :deep(.el-carousel__button) {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #b9c7d6;
    opacity: 1;
  }

  :deep(.el-carousel__indicator) {
    padding: 10px 5px;
  }

  :deep(.el-carousel__indicator.is-active .el-carousel__button) {
    width: 34px;
    height: 8px;
    background: #FF7A3C;
  }
}

.partners-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 24px 15px;
  .partner-item {
    background: #fff;
    border: 1px solid #d8dde4;
    border-radius: 4px;
    height: 78px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    padding: 10px 18px;
    box-shadow: none;
    box-sizing: border-box;
    transition: border-color 0.2s ease;

    .partner-icon {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      img {
        display: block;
        width: 48px;
        height: 48px;
        object-fit: contain;
      }
    }

    .partner-name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #26364a;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.2;
      white-space: nowrap;
    }

    &:hover {
      border-color: #4096ff;
    }
  }
}

@media (max-width: 1100px) {
  .partners-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .partners-carousel {
    :deep(.el-carousel__container) {
      height: 386px;
    }
  }
}

@media (max-width: 640px) {
  .partners-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;

    .partner-item {
      height: 68px;
      padding: 10px 12px;

      .partner-icon {
        width: 42px;
        height: 42px;

        img {
          width: 42px;
          height: 42px;
        }
      }

      .partner-name {
        font-size: 14px;
      }
    }
  }

  .partners-carousel {
    :deep(.el-carousel__container) {
      height: 478px;
    }
  }
}
</style>
